import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, Presentation, LayoutGrid, FileText, ArrowRight, X, Sparkles, Layers } from 'lucide-react';

import { PendingGen } from '@/context/pending-generations.context';

interface GenerationReadyModalProps {
  gen: PendingGen | null;
  onClose: () => void;
}

export function GenerationReadyModal({ gen, onClose }: GenerationReadyModalProps) {
  const [timeLeft, setTimeLeft] = useState(60);

  useEffect(() => {
    if (!gen) return;
    
    setTimeLeft(60);
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gen, onClose]);

  if (!gen) return null;

  const handleGo = () => {
    const target = gen.fullRoute || gen.artifactUrl;
    if (!target) {
      onClose();
      return;
    }
    if (/^(https?:|blob:|data:)/i.test(target)) {
      window.open(target, '_blank', 'noopener,noreferrer');
    } else {
      window.location.assign(target);
    }
    onClose();
  };

  const getIcon = () => {
    switch (gen.type) {
      case 'presentation': return <Presentation className="h-10 w-10 text-violet-600" />;
      case 'infographic': return <LayoutGrid className="h-10 w-10 text-violet-600" />;
      case 'test': return <CheckCircle2 className="h-10 w-10 text-green-600" />;
      case 'case': return <Sparkles className="h-10 w-10 text-amber-500" />;
      case 'flashcard': return <Layers className="h-10 w-10 text-violet-600" />;
      default: return <FileText className="h-10 w-10 text-violet-600" />;
    }
  };

  const getLabel = () => {
    switch (gen.type) {
      case 'presentation': return 'Presentación';
      case 'infographic': return 'Infografía';
      case 'test': return 'Test de examen';
      case 'case': return 'Caso práctico';
      case 'flashcard': return 'Mazo de flashcards';
      case 'report': return 'Resumen ejecutivo';
      default: return 'Material de estudio';
    }
  };

  return (
    <Dialog open={!!gen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[420px] p-0 overflow-hidden border-none bg-transparent shadow-2xl outline-none">
        <div className="relative bg-white dark:bg-slate-950 rounded-[2rem] overflow-hidden border border-white/20 shadow-2xl">
          {/* Background Decorative Circles */}
          <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-64 h-64 bg-violet-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/4 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

          {/* Header Gradient */}
          <div className="h-32 bg-gradient-to-br from-violet-600 via-violet-700 to-indigo-800 flex items-center justify-center relative">
             <div className="absolute top-4 right-4">
               <Button variant="ghost" size="icon" className="text-white/60 hover:text-white hover:bg-white/10 rounded-full h-8 w-8" onClick={onClose}>
                 <X className="h-4 w-4" />
               </Button>
             </div>
             
             {/* Glowing Icon Container */}
             <div className="relative">
                <div className="absolute inset-0 bg-white/20 blur-xl rounded-full animate-pulse" />
                <div className="relative bg-white dark:bg-slate-900 p-4 rounded-3xl shadow-2xl transform translate-y-10 border-4 border-slate-50 dark:border-slate-800">
                  {getIcon()}
                </div>
             </div>
          </div>

          <div className="pt-16 pb-8 px-8 text-center space-y-6">
            <div className="space-y-2">
              <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">¡Ya lo tienes!</h3>
              <p className="text-[15px] text-slate-500 dark:text-slate-400 leading-relaxed px-4">
                Tu {getLabel().toLowerCase()} sobre <span className="font-bold text-slate-800 dark:text-slate-200">"{gen.topic}"</span> se ha generado con éxito.
              </p>
            </div>

            <div className="flex flex-col gap-3 pt-2">
              <Button 
                onClick={handleGo}
                className="w-full h-14 bg-violet-600 hover:bg-violet-700 text-white font-bold text-base rounded-2xl shadow-xl shadow-violet-200 dark:shadow-none transition-all hover:scale-[1.02] active:scale-[0.98] group"
              >
                Ver material ahora
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button variant="ghost" onClick={onClose} className="h-10 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 text-xs font-medium uppercase tracking-widest">
                Cerrar aviso
              </Button>
            </div>

            {/* Timer Progress */}
            <div className="pt-2 space-y-3">
              <div className="flex justify-between text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] font-bold">
                <span>Auto-cierre en</span>
                <span>{timeLeft} segundos</span>
              </div>
              <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 transition-all duration-1000 ease-linear"
                  style={{ width: `${(timeLeft / 60) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
